import { Component, effect, inject, input, output, signal, ViewChild} from '@angular/core';
import { MessageService } from 'primeng/api';
import { FileBeforeUploadEvent, FileSelectEvent, FileUpload, FileUploadEvent } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { Dialog } from 'primeng/dialog';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StorageManager } from '../../storage-manager.class';

interface UploadEvent {
  originalEvent: any;
  files: File[];
}

export interface OptionsComponent {
  name: string;
  directory: string;
  multiple: boolean;
  accept: string;
  maxFileSize: number;
  mode: 'advanced' | 'basic' | undefined;
  url: string;
  uploaded: boolean;
  uploadedFilesNames?: string[];
  uploadFunction?: (event: FileUploadEvent) => void | null;
  fileFieldName?: string;
  clientIdResolver?: () => string | number | null | undefined;
}

@Component({
  selector: 'app-upload-file',
  imports: [
    FileUpload,
    ToastModule,
    CommonModule,
    Dialog
],
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.scss',
  standalone: true,
  providers: [ MessageService ],
})
export class UploadFileComponent  {

  private static readonly DEFAULT_FILE_FIELD_NAME = 'uploadFile';

  private hasSkippedInitialFileNamesEmit = false;

  private wasUploadDialogVisible = false;

  private lastFileListRefreshKey = '';

  @ViewChild('fileUpload') fileUpload!: FileUpload;

  showUploadDialog = input.required<boolean>();

  options = input.required<OptionsComponent>();

  files = signal<File[]>([]);

  nameFile = signal<string[]>([]);

  hasInvalidFileNames = signal<boolean>(false);

  invalidFileNames = signal<string[]>([]);

  title = input<String>('Subir Archivo');

  eventUpload = output<UploadEvent>();

  eventClose = output<void>();

  namesFilesOutput = output<string[]>();

  http = inject(HttpClient);

  messageService = inject(MessageService);

  constructor(){
    effect(() => {
      const currentNameFile = this.nameFile();

      if (!this.hasSkippedInitialFileNamesEmit) {
        this.hasSkippedInitialFileNamesEmit = true;
        return;
      }

      this.namesFilesOutput.emit(currentNameFile);
    });

    effect(() => {
      const isDialogVisible = this.showUploadDialog();
      const uploadTargetName = this.resolveUploadTargetName();
      const refreshKey = `${this.options().directory}/${uploadTargetName}`;

      if (isDialogVisible && uploadTargetName.length > 0
          && (!this.wasUploadDialogVisible || this.lastFileListRefreshKey !== refreshKey)) {
        this.lastFileListRefreshKey = refreshKey;
        this.refreshUploadedFilesFromServer();
      }

      this.wasUploadDialogVisible = isDialogVisible;

      if (!isDialogVisible) {
        this.lastFileListRefreshKey = '';
      }
    });
  }

  onUpload(event: FileUploadEvent): void {
    // Block upload if there are invalid names
    if (this.hasInvalidFileNames()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Subida bloqueada',
        detail: 'Corrige los nombres de archivo antes de subir',
        life: 3000
      });
      return;
    }

    const serverFileNames = this.extractManagedFileNames(this.extractResponseBody(event.originalEvent));
    const uploadedFileNames = this.extractUploadedFileNames(event);
    const selectedFileNames = event.files.map(file => file.name);
    const nextFileNames = serverFileNames.length > 0
      ? serverFileNames
      : this.mergeFileNames(this.getCurrentUploadedFileNames(), uploadedFileNames.length > 0 ? uploadedFileNames : selectedFileNames);

    this.files.set([...event.files]);
    this.updateUploadedFileNames(nextFileNames);
    this.hasInvalidFileNames.set(false);
    this.invalidFileNames.set([]);

    this.messageService.add({severity: 'info', summary: 'Archivo(s) Subido', detail: ''});

    this.eventUpload.emit({ originalEvent: event.originalEvent, files: event.files });
  }

  onBeforeUpload(event: FileBeforeUploadEvent): void {
    this.appendUploadMetadata(event.formData);
  }

  onUploadError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error de subida',
      detail: 'No se ha podido subir el archivo. Revisa que el archivo siga seleccionado e inténtalo de nuevo.',
      life: 5000
    });
  }

  getUploadFileFieldName(): string {
    const configuredFileFieldName = this.options().fileFieldName ?? UploadFileComponent.DEFAULT_FILE_FIELD_NAME;
    const normalizedFileFieldName = configuredFileFieldName.endsWith('[]')
      ? configuredFileFieldName.slice(0, -2)
      : configuredFileFieldName;

    return this.options().multiple ? `${normalizedFileFieldName}[]` : normalizedFileFieldName;
  }

  getUploadUrl(): string {
    const options = this.options();
    const uploadTargetName = this.resolveUploadTargetName();
    let uploadUrl = options.url;

    uploadUrl = this.appendQueryParamIfMissing(uploadUrl, 'client', uploadTargetName);
    uploadUrl = this.appendQueryParamIfMissing(uploadUrl, 'module', options.directory);

    return uploadUrl;
  }

  canUpload(): boolean {
    return !this.hasInvalidFileNames() && this.resolveUploadTargetName().length > 0;
  }

  hasUploadedFiles(): boolean {
    return this.getCurrentUploadedFileNames().length > 0;
  }

  getCurrentUploadedFileNames(): string[] {
    const currentNames = this.nameFile();

    if (currentNames.length > 0) {
      return currentNames;
    }

    return this.normalizeFileNameList(this.options().uploadedFilesNames ?? []);
  }

  onSelect(event: FileSelectEvent): void {

    if (event.currentFiles && event.currentFiles.length > 0) {

      const fileNames = event.currentFiles.map(file => file.name);
      const invalidFiles = this.validateFileNames(fileNames);

      if (invalidFiles.length > 0) {

        this.hasInvalidFileNames.set(true);
        this.invalidFileNames.set(invalidFiles);
        this.fileUpload.clear();

        this.messageService.add({
          severity: 'error',
          summary: 'Nombres de archivo inválidos',
          detail: 'Los archivos no deben contener espacios ni caracteres especiales',
          life: 5000
        });
      } else {
        this.hasInvalidFileNames.set(false);
        this.invalidFileNames.set([]);
        this.files.set([...event.currentFiles]);
      }

    } else {
        this.files.set([]);
        this.hasInvalidFileNames.set(false);
        this.invalidFileNames.set([]);
    }
  }

  deleteUploadedFile(fileName: string): void {
    const params = this.buildFileManagementParams(fileName);

    if (params === null) {
      return;
    }

    this.http.delete<Record<string, unknown>>(environment.serverUrl + 'ws/index.php', { params }).subscribe({
      next: (response) => {
        if (!this.isSuccessfulResponse(response)) {
          this.messageService.add({
            severity: 'error',
            summary: 'No se ha eliminado',
            detail: this.getResponseMessage(response, 'No se ha podido eliminar el archivo.'),
            life: 5000
          });
          return;
        }

        const responseFileNames = this.extractManagedFileNames(response);
        const nextFileNames = responseFileNames.length > 0
          ? responseFileNames
          : this.getCurrentUploadedFileNames().filter((it) => it !== fileName);

        this.updateUploadedFileNames(nextFileNames);
        this.messageService.add({
          severity: 'success',
          summary: 'Archivo eliminado',
          detail: fileName,
          life: 3000
        });
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.refreshUploadedFilesFromServer();
          this.messageService.add({
            severity: 'warn',
            summary: 'Archivo no encontrado',
            detail: 'El archivo ya no existe en el servidor. Se ha actualizado la lista.',
            life: 5000
          });
          return;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'No se ha eliminado',
          detail: 'No se ha podido eliminar el archivo. Revisa la conexión e inténtalo de nuevo.',
          life: 5000
        });
      }
    });
  }

  renameUploadedFile(fileName: string): void {
    const newFileName = String(window.prompt('Nuevo nombre del archivo', fileName) ?? '').trim();

    if (newFileName.length === 0 || newFileName === fileName) {
      return;
    }

    const invalidFileNames = this.validateFileNames([newFileName]);

    if (invalidFileNames.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Nombre de archivo inválido',
        detail: 'El archivo no debe contener espacios ni caracteres especiales.',
        life: 5000
      });
      return;
    }

    const params = this.buildFileManagementParams(fileName, newFileName);

    if (params === null) {
      return;
    }

    this.http.put<Record<string, unknown>>(environment.serverUrl + 'ws/index.php', null, { params }).subscribe({
      next: (response) => {
        if (!this.isSuccessfulResponse(response)) {
          this.messageService.add({
            severity: 'error',
            summary: 'No se ha renombrado',
            detail: this.getResponseMessage(response, 'No se ha podido renombrar el archivo.'),
            life: 5000
          });
          return;
        }

        const responseFileNames = this.extractManagedFileNames(response);
        const resolvedFileName = responseFileNames[0] ?? this.extractManagedFileName(response) ?? newFileName;
        const nextFileNames = responseFileNames.length > 0
          ? responseFileNames
          : this.getCurrentUploadedFileNames().map((it) => it === fileName ? resolvedFileName : it);

        this.updateUploadedFileNames(nextFileNames);
        this.messageService.add({
          severity: 'success',
          summary: 'Archivo renombrado',
          detail: resolvedFileName,
          life: 3000
        });
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.refreshUploadedFilesFromServer();
          this.messageService.add({
            severity: 'warn',
            summary: 'Archivo no encontrado',
            detail: 'El archivo original ya no existe. Se ha actualizado la lista.',
            life: 5000
          });
          return;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'No se ha renombrado',
          detail: 'No se ha podido renombrar el archivo. Revisa la conexión e inténtalo de nuevo.',
          life: 5000
        });
      }
    });
  }

  private appendUploadMetadata(formData: FormData): void {
    const options = this.options();
    const uploadTargetName = this.resolveUploadTargetName();

    if (uploadTargetName.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cliente no disponible',
        detail: 'No se ha podido determinar la carpeta de destino del archivo.',
        life: 5000
      });

      return;
    }

    this.appendFormValueIfMissing(formData, 'folder', options.directory);
    this.appendFormValueIfMissing(formData, 'module', options.directory);
    this.appendFormValueIfMissing(formData, 'client', uploadTargetName);
  }

  private resolveUploadTargetName(): string {
    const configuredName = this.normalizeUploadTargetName(this.options().name);

    if (configuredName.length > 0) {
      return configuredName;
    }

    const resolvedClientTargetName = this.buildUploadTargetNameFromClientId(this.options().clientIdResolver?.());

    if (resolvedClientTargetName.length > 0) {
      return resolvedClientTargetName;
    }

    const storageClientTargetName = this.buildUploadTargetNameFromStorageClient();

    if (storageClientTargetName.length > 0) {
      return storageClientTargetName;
    }

    return this.buildUploadTargetNameFromStorageUser();
  }

  private normalizeUploadTargetName(uploadTargetName: string | null | undefined): string {
    const normalizedName = String(uploadTargetName ?? '').trim();
    const invalidNames = new Set(['u_', 'u_undefined', 'u_null', 'undefined', 'null']);

    return invalidNames.has(normalizedName.toLowerCase()) ? '' : normalizedName;
  }

  private buildUploadTargetNameFromStorageClient(): string {
    if (!environment.features.hasClients) {
      return '';
    }

    return this.buildUploadTargetNameFromClientId(StorageManager.getClient()?.id);
  }

  private buildUploadTargetNameFromStorageUser(): string {
    if (environment.features.hasClients) {
      return '';
    }

    return this.buildUploadTargetNameFromClientId(StorageManager.getUser()?.id);
  }

  private buildUploadTargetNameFromClientId(clientId: string | number | null | undefined): string {
    const normalizedClientId = String(clientId ?? '').trim();

    return normalizedClientId.length > 0 ? `u_${normalizedClientId}` : '';
  }

  private appendFormValueIfMissing(formData: FormData, fieldName: string, fieldValue: string): void {
    const normalizedValue = fieldValue.trim();

    if (normalizedValue.length === 0 || formData.has(fieldName)) {
      return;
    }

    formData.append(fieldName, normalizedValue);
  }

  private appendQueryParamIfMissing(url: string, fieldName: string, fieldValue: string): string {
    const normalizedValue = fieldValue.trim();

    if (normalizedValue.length === 0 || new RegExp(`[?&]${fieldName}=`).test(url)) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';

    return `${url}${separator}${fieldName}=${encodeURIComponent(normalizedValue)}`;
  }

  /**
   * Valida que los nombres de archivo solo contengan caracteres alfanuméricos, guiones y puntos
   * @param fileNames Array de nombres de archivos
   * @returns Array de nombres inválidos
   */
  private validateFileNames(fileNames: string[]): string[] {
    // Regex: solo permite letras, números, guiones, guiones bajos y puntos
    // No permite espacios ni caracteres especiales como @, #, %, etc.
    const validFileNameRegex = /^[a-zA-Z0-9._-]+$/;

    return fileNames.filter(fileName => !validFileNameRegex.test(fileName));
  }

  checkDirectoryIsEmpty(): void {
    this.refreshUploadedFilesFromServer();
  }

  private refreshUploadedFilesFromServer(): void {
    const params = this.buildFileManagementBaseParams();

    if (params === null) {
      return;
    }

    this.http.get<Record<string, unknown>>(environment.serverUrl + 'ws/index.php', { params }).subscribe({
      next: (response: Record<string, unknown>) => {
        if (!this.isSuccessfulResponse(response)) {
          return;
        }

        this.updateUploadedFileNames(this.extractManagedFileNames(response));
      },
      error: (error: unknown) => {
        console.error('Error al verificar el archivo en el servidor:', error);
      }
    });
  }

  openWindowUploadHelp( filename: string ): void {
    const uploadTargetName = this.resolveUploadTargetName();

    if (uploadTargetName.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cliente no disponible',
        detail: 'No se ha podido determinar la carpeta del archivo.',
        life: 5000
      });

      return;
    }

    const uploadPath = `${environment.serverUrl}uploads/${this.options().directory}/${uploadTargetName}/${filename}`;

    window.open(uploadPath, '_blank');
  }

  private updateUploadedFileNames(fileNames: string[]): void {
    const normalizedFileNames = this.normalizeFileNameList(fileNames);
    const options = this.options();

    options.uploaded = normalizedFileNames.length > 0;
    options.uploadedFilesNames = normalizedFileNames;
    this.nameFile.set(normalizedFileNames);
  }

  private normalizeFileNameList(fileNames: string[]): string[] {
    const normalizedFileNames: string[] = [];

    for (const fileName of fileNames) {
      const normalizedFileName = String(fileName ?? '').trim();

      if (normalizedFileName.length === 0 || normalizedFileNames.includes(normalizedFileName)) {
        continue;
      }

      normalizedFileNames.push(normalizedFileName);
    }

    return normalizedFileNames;
  }

  private mergeFileNames(currentFileNames: string[], nextFileNames: string[]): string[] {
    return this.normalizeFileNameList([...currentFileNames, ...nextFileNames]);
  }

  private buildFileManagementBaseParams(): HttpParams | null {
    const uploadTargetName = this.resolveUploadTargetName();

    if (uploadTargetName.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cliente no disponible',
        detail: 'No se ha podido determinar la carpeta del archivo.',
        life: 5000
      });
      return null;
    }

    return new HttpParams()
      .set('p1', 'upload')
      .set('p2', 'upload_file')
      .set('folder', this.options().directory)
      .set('client', uploadTargetName)
      .set('module', this.options().directory);
  }

  private buildFileManagementParams(fileName: string, newFileName?: string): HttpParams | null {
    const normalizedFileName = String(fileName ?? '').trim();
    let params = this.buildFileManagementBaseParams();

    if (params === null || normalizedFileName.length === 0) {
      return null;
    }

    params = params.set('file', normalizedFileName);

    if (newFileName !== undefined) {
      params = params.set('newFile', newFileName);
    }

    return params;
  }

  private extractUploadedFileNames(event: FileUploadEvent): string[] {
    const responseBody = this.extractResponseBody(event.originalEvent);
    const data = this.extractDataRecord(responseBody);
    const fileNames = [
      ...this.extractStringArray(data['fileNames']),
      ...this.extractFileNamesFromCollection(data['files']),
      ...this.extractFileNamesFromCollection(data['resultados']),
      ...this.extractFileNamesFromCollection(data['results']),
    ];

    return this.normalizeFileNameList(fileNames);
  }

  private extractManagedFileName(response: unknown): string {
    return this.extractManagedFileNames(response)[0] ?? '';
  }

  private extractManagedFileNames(response: unknown): string[] {
    const data = this.extractDataRecord(response);
    const candidates = [
      String(data['fileName'] ?? ''),
      String(data['archivo'] ?? ''),
      ...this.extractStringArray(data['fileNames']),
      ...this.extractFileNamesFromCollection(data['files']),
      ...this.extractFileNamesFromCollection(data['resultados']),
      ...this.extractFileNamesFromCollection(data['results']),
    ];

    return this.normalizeFileNameList(candidates);
  }

  private extractResponseBody(originalEvent: unknown): unknown {
    if (this.isRecord(originalEvent) && this.isRecord(originalEvent['body'])) {
      return originalEvent['body'];
    }

    return originalEvent;
  }

  private extractDataRecord(response: unknown): Record<string, unknown> {
    if (this.isRecord(response) && this.isRecord(response['data'])) {
      return response['data'];
    }

    return this.isRecord(response) ? response : {};
  }

  private extractStringArray(collection: unknown): string[] {
    if (!Array.isArray(collection)) {
      return [];
    }

    return collection.map((item) => String(item ?? '').trim()).filter((item) => item.length > 0);
  }

  private extractFileNamesFromCollection(collection: unknown): string[] {
    if (!Array.isArray(collection)) {
      return [];
    }

    const fileNames: string[] = [];

    for (const item of collection) {
      if (typeof item === 'string') {
        fileNames.push(item);
        continue;
      }

      if (!this.isRecord(item)) {
        continue;
      }

      const candidates = [item['fileName'], item['archivo'], item['originalName']];

      for (const candidate of candidates) {
        const fileName = String(candidate ?? '').trim();

        if (fileName.length > 0) {
          fileNames.push(fileName);
          break;
        }
      }
    }

    return fileNames;
  }

  private isSuccessfulResponse(response: Record<string, unknown>): boolean {
    const statusCode = Number(response['statusCode'] ?? 0);

    return statusCode >= 200 && statusCode < 300;
  }

  private getResponseMessage(response: Record<string, unknown>, fallbackMessage: string): string {
    const statusMessage = String(response['statusMessage'] ?? '').trim();

    return statusMessage.length > 0 ? statusMessage : fallbackMessage;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
